import type * as React from 'react';
import { put } from '@Domain/Api';
import subjectMemberSaved, {
	type SubjectMemberSavedAction,
} from '@Application/Subjects/Members/Actions/subjectMemberSaved';
import subjectMemberSaveFailed, {
	type SubjectMemberSaveFailedAction,
} from '@Application/Subjects/Members/Actions/subjectMemberSaveFailed';
import type PutSubjectMember from '@Application/Subjects/Members/Commands/PutSubjectMember';
import buildSubjectApiPath from '@Application/Subjects/buildSubjectApiPath';
import fetchSubjectMembers from '@Application/Subjects/Members/fetchSubjectMembers';
import fetchSubjectUsers from '@Application/Subjects/Members/fetchSubjectUsers';
import FetchSubjectMembers from '@Application/Subjects/Members/Queries/FetchSubjectMembers';
import FetchSubjectUsers from '@Application/Subjects/Members/Queries/FetchSubjectUsers';
import type { SubjectMembersFetchedAction } from '@Application/Subjects/Members/Actions/subjectMembersFetched';
import type { SubjectMembersFetchFailedAction } from '@Application/Subjects/Members/Actions/subjectMembersFetchFailed';
import type { SubjectUsersFetchedAction } from '@Application/Subjects/Members/Actions/subjectUsersFetched';
import type { SubjectUsersFetchFailedAction } from '@Application/Subjects/Members/Actions/subjectUsersFetchFailed';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';

type Actions =
	| SubjectMemberSavedAction
	| SubjectMemberSaveFailedAction
	| SubjectMembersFetchedAction
	| SubjectMembersFetchFailedAction
	| SubjectUsersFetchedAction
	| SubjectUsersFetchFailedAction;

/** No list in the response, so members and candidates are refetched; a failed refetch is ignored. */
export default (command: PutSubjectMember) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			await put<void>(
				`${buildSubjectApiPath(command.facilityId, command.subject)}/members/${command.userId}`,
				command.toJSON(),
			);

			const action = subjectMemberSaved(command.subject.subjectId, command.userId);
			dispatch(action);
			await fetchSubjectMembers(new FetchSubjectMembers(command.facilityId, command.subject))(
				dispatch,
			).catch(() => undefined);
			await fetchSubjectUsers(new FetchSubjectUsers(command.facilityId, command.subject))(
				dispatch,
			).catch(() => undefined);

			return action;
		} catch (error) {
			const action = subjectMemberSaveFailed(extractMessages(error));
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
